import React, { useState } from 'react';
import useLanguage from '../../hooks/useLanguage';
import { moneyExact, money, percent, count } from './pricingFormat';
import { billsOnVatInclusive as billsOnVatInclusiveBasis } from '../../config/moneyModel';

/**
 * The stay's money — one table, one discount lever.
 *
 * ⚠️ ONE lever, and it is the whole point of this rewrite. There used to be two discount
 * boxes on the page: the quote's, measured off the ex-VAT room total and moving all three
 * columns, and this panel's, measured off the already-discounted VAT-inclusive guest price
 * and moving one. They compounded silently — a 15% tier plus a 10% offer is 23.5% off the
 * base, and no figure on the screen said so; only the floor warning stood behind it. The
 * box here is now pre-filled with the unit's OWN tier percent and always measured off
 * `basisTotal`, so at the tier value it reproduces the server's figures exactly and any
 * deviation is visible as one number, not as two that have to be multiplied together.
 *
 * ⚠️ It is an INPUT, never a suggestion. What share of the platform saving to pass to the
 * guest is a commercial judgement per booking and the system has no evidence to make it
 * (decision 14). Ops types the number; the page does the arithmetic, stores nothing, and
 * creates no booking.
 *
 * ⚠️ Every rate arrives in `economics.rates` — the backend's money model, transmitted.
 * Re-declaring 25% / 15% here would be a fourth copy of it, which is exactly the drift
 * `pricingEconomics.js` exists to prevent.
 */
// Top to bottom: guest pays − VAT = revenue, then revenue − fee − commission = owner net.
// Without the VAT line the first subtraction has no visible operand, so ticking the box
// moved owner net with nothing on screen explaining why.
//
// ⚠️ `commissionBase` sits inside that column but is NOT a term of the subtraction — it is
// what the commission is a percentage OF, and it sits directly above the commission
// because the fee is 25% of THAT row, not of the room-revenue row two above it. Without it
// ops reads 625.60 beside 2,176.00 and concludes the panel is broken. The basis is read
// off the payload, never assumed here: it was VAT-inclusive 2026-08-20 → 2026-09-09 and is
// VAT-exclusive since (tasks/vat-exclusive-fee-basis.md), and this row's footing label follows it.
//
// What the guest SAVES is deliberately not a row: the note under the table already says
// it, with the two prices it sits between and the percentage, and it is the one thing on
// this page written to be read aloud.
const ROWS = ['vat', 'roomRevenueExVat', 'otaFee', 'commissionBase', 'hostMeCommission', 'ownerNet'];

const round2 = (v) => Math.round(v * 100) / 100;
const bad = (v) => v === '' || !Number.isFinite(Number(v)) || Number(v) < 0;

const ChannelEconomics = ({ economics, nights, floor, basisTotal, tierDiscountPercent, longStay }) => {
  const { t, isArabic } = useLanguage();

  const { rates, scenarioGuestPrice } = economics;

  // A what-if about VAT, not a ruling on it: it shows what a guest would pay on a unit
  // that does not charge VAT. Whether we MAY quote that way is the accountant's answer,
  // which is why nothing here is saved. Scoped to the picked column — the only one whose
  // price ops controls.
  const [vatOn, setVatOn] = useState(true);

  // Which column the offer belongs to. An enquiry arrives on ONE channel, and the offer
  // used to always land in the direct column — the wrong one whenever the guest was
  // messaging from Airbnb or Booking, which is a large share of the real negotiations.
  //
  // ⚠️ This picks the TARGET, it does not create per-channel pricing (decision 28). The
  // two channels not picked stay where they were: they are the benchmark the offer is
  // judged against, and a benchmark that moves with the thing it measures tells ops
  // nothing.
  const [offerChannel, setOfferChannel] = useState('direct');

  // What the discount comes OFF — the unit's base for these nights, before anything.
  // Guarded: a zero base would make every percentage meaningless, and the quote is only
  // rendered complete when it has one.
  const base = typeof basisTotal === 'number' && basisTotal > 0 ? basisTotal : null;
  const guestPriceAt = (roomTotal, vat) => (vat ? round2(roomTotal * (1 + rates.vat)) : roomTotal);

  // The three fields are one number wearing three faces. The PERCENT is the anchor: it is
  // the only one of them that means the same thing with VAT on or off, which is why the
  // tick recomputes the other two from it rather than re-labelling what they already show.
  const fieldsFor = (pct, vat) => {
    if (!base) return { total: '', perNight: '' };
    const roomTotal = round2(base * (1 - pct / 100));
    const total = guestPriceAt(roomTotal, vat);
    return {
      total: String(total),
      perNight: nights > 0 ? String(round2(total / nights)) : ''
    };
  };

  const initialPct = tierDiscountPercent > 0 ? tierDiscountPercent : 0;
  const initialFields = base ? fieldsFor(initialPct, true) : { total: '', perNight: '' };

  // Pre-filled with the unit's own answer, not empty. An empty box beside a table full of
  // discounted figures is what made two discounts possible to hold at once without seeing
  // it: the tier had already fired, and the box that said nothing looked like nothing had.
  const [discountPercent, setDiscountPercent] = useState(initialPct > 0 ? String(initialPct) : '');
  const [guestTotal, setGuestTotal] = useState(initialFields.total);
  const [perNight, setPerNight] = useState(initialFields.perNight);

  const pctFromTotal = (total, vat) => {
    if (bad(total) || !base) return '';
    const roomTotal = vat ? Number(total) / (1 + rates.vat) : Number(total);
    const pct = round2((1 - roomTotal / base) * 100);
    // A total ABOVE the base is not a negative discount anyone would read out to a guest,
    // so the box simply empties rather than showing one.
    return pct > 0 ? String(pct) : '';
  };

  const onTotal = (e) => {
    const v = e.target.value;
    setGuestTotal(v);
    setPerNight(bad(v) || !nights ? '' : String(round2(Number(v) / nights)));
    setDiscountPercent(pctFromTotal(v, vatOn));
  };
  const onPerNight = (e) => {
    const v = e.target.value;
    setPerNight(v);
    const total = bad(v) ? '' : String(round2(Number(v) * nights));
    setGuestTotal(total);
    setDiscountPercent(pctFromTotal(total, vatOn));
  };
  const onDiscountPercent = (e) => {
    const v = e.target.value;
    setDiscountPercent(v);
    const p = Number(v);
    // Cleared = back to the unit's own discount, which is what the box was pre-filled with.
    // 100% is a free stay and beyond it the price goes negative — neither is a quote.
    if (v === '' || !Number.isFinite(p) || p < 0 || p >= 100) {
      const back = fieldsFor(v === '' ? initialPct : 0, vatOn);
      setGuestTotal(v === '' ? back.total : '');
      setPerNight(v === '' ? back.perNight : '');
      return;
    }
    const next = fieldsFor(p, vatOn);
    setGuestTotal(next.total);
    setPerNight(next.perNight);
  };
  const onVatToggle = (e) => {
    const vat = e.target.checked;
    setVatOn(vat);
    // Recomputed from the percent, never re-labelled. Leaving the figures alone would keep
    // the guest paying the same number and hand the tax to the owner, which inverts the
    // point of the toggle: it exists so ops can show a genuinely cheaper price.
    const p = bad(discountPercent) ? initialPct : Number(discountPercent);
    const next = fieldsFor(p, vat);
    setGuestTotal(next.total);
    setPerNight(next.perNight);
  };

  // What an emptied or out-of-range box falls back to: the unit's own answer, ON THE
  // CURRENT FOOTING. `scenarioGuestPrice` is VAT-INCLUSIVE, so using it directly showed an
  // inclusive figure under a «بدون ضريبة» label the moment the tick came off.
  const fallbackGuestPrice = Number(fieldsFor(initialPct, vatOn).total) || 0;
  const guestPays = bad(guestTotal) ? fallbackGuestPrice : round2(Number(guestTotal));

  // The guest price is what ops types; room revenue is derived back out of it. With VAT
  // off the two are the same figure — there is no tax sitting inside the price.
  const roomRevenue = round2(vatOn ? guestPays / (1 + rates.vat) : guestPays);

  // An OFFER is a deviation from what the unit already answers, not merely a filled box.
  // The box is pre-filled now, so "touched" cannot mean what it used to: at the tier value
  // this must behave exactly as an untouched panel did — three benchmark columns, no
  // saving sentence.
  const overridden = !vatOn || Math.abs(guestPays - scenarioGuestPrice) >= 0.005;
  // Narrower than `overridden`: the ROOM price actually moved. Unticking VAT changes what
  // the guest pays without replacing the calendar's night-by-night variation, so the
  // flat-rate caution does not belong to it.
  const priceChanged = Math.abs(roomRevenue - economics.direct.roomRevenueExVat) >= 0.005;

  // What our fee is charged on. Read from the basis the BACKEND shipped — never a literal
  // here. The billing worksheet reads the same constant server-side, and the two being one
  // number is what stops ops quoting an owner a fee his bill later contradicts.
  //
  // ⚠️ With the VAT what-if OFF this is the room revenue itself, and that is deliberate:
  // the toggle asks "what if this unit charges no VAT", and there is no VAT to include. It
  // mirrors the billing rule for a direct booking whose VAT was never established.
  // Mirrored constant, never a literal — `web/src/config/moneyModel.js`. On deploy skew
  // (a cached payload with no `revenueBasis`) it must fall to the basis the SERVER is on,
  // or the picked column computes on one base while the two beside it sit on the other —
  // a 15% mismatch inside one table, silently. Since 2026-09-09 that basis is VAT-EXCLUSIVE
  // (tasks/vat-exclusive-fee-basis.md); the fallback moves with the constant, never ahead of it.
  const billsOnVatInclusive = rates.revenueBasis == null
    ? false
    : billsOnVatInclusiveBasis(rates.revenueBasis);
  const commissionBase = round2(billsOnVatInclusive ? guestPays : roomRevenue);
  const commission = round2(commissionBase * rates.commission);

  // The offer, priced on the channel it was made on. The platform's fee is the ONLY thing
  // that differs between channels here — our 25% applies either way, which is the whole
  // point the panel exists to make.
  // ⚠️ Falls back to the rate implied by the column itself, never to zero. A cached payload
  // from before this release carries no `rates.otaFee`, and `?? 0` would have priced an
  // Airbnb offer with NO platform fee — inflating owner-net by ~17.8% on the one column ops
  // was actively negotiating.
  const impliedFeeRate = (key) => {
    const col = economics[key];
    return col && col.roomRevenueExVat > 0 ? col.otaFee / col.roomRevenueExVat : 0;
  };
  const offerFeeRate = rates.otaFee?.[offerChannel] ?? impliedFeeRate(offerChannel);
  const offerOtaFee = round2(roomRevenue * offerFeeRate);
  const offer = {
    guestPays,
    roomRevenueExVat: roomRevenue,
    otaFee: offerOtaFee,
    commissionBase,
    // The owner's net still comes out of the VAT-EXCLUSIVE room revenue — the VAT was
    // never his money. Only the fee's BASE moved.
    hostMeCommission: commission,
    ownerNet: round2(roomRevenue - offerOtaFee - commission)
  };

  // VAT is what the guest paid that never belonged to us — derived, not a second source of
  // truth, so it cannot drift from the two figures it sits between.
  const withVat = (c) => ({ ...c, vat: round2(c.guestPays - c.roomRevenueExVat) });

  const columns = ['direct', 'airbnb', 'booking'].map(key => ({
    key,
    data: withVat(overridden && key === offerChannel ? offer : economics[key]),
    isOffer: key === offerChannel
  }));

  // ⚠️ Evaluated ALWAYS, not only on a deviation. With one lever the quoted price and the
  // offered price are the same number, so this is the page's only floor check — and it is
  // measured against what ops is about to offer, which is the number that matters.
  // Advisory by decision (decision 10): a launch offer deliberately goes under the floor.
  const nightly = nights > 0 ? round2(offer.roomRevenueExVat / nights) : null;
  const belowFloor = typeof floor === 'number' && nightly !== null && nightly < floor;

  const given = round2(scenarioGuestPrice - offer.guestPays);
  const givenPercent = scenarioGuestPrice > 0 ? (given / scenarioGuestPrice) * 100 : 0;

  const inputStyle = { textAlign: isArabic ? 'right' : 'left' };
  // Both money boxes are quoted to the guest VAT-inclusive; the figure a PLATFORM is given
  // is the ex-VAT one, and until now it existed only inside the floor check. Suppressed
  // when the tick is off — the box then already IS the ex-VAT figure, and repeating it
  // under itself is the duplication this rewrite removes.
  const exVatLine = (value) => (vatOn && typeof value === 'number' ? (
    <div className="pg-policy__sub">
      {t('pricing.common.exVat')} <em>{moneyExact(value)}</em>
    </div>
  ) : null);

  return (
    <div className="pg-econ">
      {/* The offer, and the channel it belongs to. The tick and the boxes govern the PICKED
          column only — never all three — so the two beside it stay readable as the
          comparison they exist to be. */}
      <div className="pg-scenario">
        <div className="pg-scenario__channels" role="group" aria-label={t('pricing.direct.channelLabel')}>
          <span className="pg-block__hint">{t('pricing.direct.channelLabel')}</span>
          {['direct', 'airbnb', 'booking'].map(key => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm ${offerChannel === key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setOfferChannel(key)}
            >
              {t(`pricing.economics.${key}`)}
            </button>
          ))}
        </div>
        <div className="pg-scenario__form">
          <div className="pg-policy__field">
            <label htmlFor="pg-direct-discount">{t('pricing.direct.discountPercent')}</label>
            <input
              id="pg-direct-discount"
              type="number"
              min="0"
              max="99"
              className="form-control pg-scenario__lever"
              style={inputStyle}
              value={discountPercent}
              onChange={onDiscountPercent}
            />
            {/* Where the number in the box came from. A deviation has to be visible AS one,
                or the box reads as a value someone chose deliberately either way. */}
            {tierDiscountPercent > 0 && (
              <div className="pg-policy__sub">
                {(discountPercent === '' || Number(discountPercent) === tierDiscountPercent)
                  && longStay?.minNights
                  ? `${t('pricing.quote.tier')} ${t('pricing.quote.tierFrom')} ${count(longStay.minNights)} ${t('pricing.common.nights')}`
                  : `${t('pricing.direct.unitDiscount')} ${tierDiscountPercent}%`}
              </div>
            )}
          </div>
          <div className="pg-policy__field">
            {/* The only money fields on the page that carried no footing — so nothing told
                ops whether to type the all-in price or the pre-VAT one. */}
            <label htmlFor="pg-direct-total">
              {t('pricing.direct.total')}{' '}
              <span className="pg-footing">
                {vatOn ? t('pricing.common.incVat') : t('pricing.direct.noVat')}
              </span>
            </label>
            <input
              id="pg-direct-total"
              type="number"
              min="0"
              className="form-control"
              style={inputStyle}
              placeholder={moneyExact(fallbackGuestPrice)}
              value={guestTotal}
              onChange={onTotal}
            />
            {exVatLine(offer.roomRevenueExVat)}
          </div>
          <div className="pg-policy__field">
            <label htmlFor="pg-direct-nightly">
              {t('pricing.direct.perNight')}{' '}
              <span className="pg-footing">
                {vatOn ? t('pricing.common.incVat') : t('pricing.direct.noVat')}
              </span>
            </label>
            <input
              id="pg-direct-nightly"
              type="number"
              min="0"
              className="form-control"
              style={inputStyle}
              placeholder={nights > 0 ? moneyExact(round2(fallbackGuestPrice / nights)) : undefined}
              value={perNight}
              onChange={onPerNight}
            />
            {exVatLine(nightly)}
          </div>
          <div className="form-check pg-scenario__vat">
            <input
              className="form-check-input"
              type="checkbox"
              id="pg-direct-vat"
              checked={vatOn}
              onChange={onVatToggle}
            />
            <label className="form-check-label" htmlFor="pg-direct-vat">
              {t('pricing.direct.vatOn')}
            </label>
          </div>
        </div>
        {/* Only when it is OFF — a caution beside a ticked box is noise, and the moment it
            matters is the moment someone has decided this stay carries no tax. */}
        {!vatOn && (
          <p className="pg-block__hint">{t('pricing.direct.vatCaution')}</p>
        )}
      </div>

      {/* One flat rate replaces the calendar's night-by-night variation. On a stay spanning
          a weekend that quietly gives away the expensive nights. */}
      {priceChanged && nights > 1 && (
        <p className="pg-block__hint">{t('pricing.direct.flatRateNote')}</p>
      )}

      <table className="pg-table">
        <thead>
          <tr>
            <th />
            {columns.map(c => (
              <th key={c.key} className={c.isOffer ? 'pg-econ__picked' : undefined}>
                {t(`pricing.economics.${c.key}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Rendered apart from the rest because its footing is per COLUMN, not per row:
              the tick governs the PICKED price only, so unticking it left the other two
              labelled «بدون ضريبة» while both still included VAT. */}
          <tr>
            <td className="pg-econ__label">{t('pricing.economics.guestPays')}</td>
            {columns.map(c => (
              <td key={c.key} data-figure className={c.isOffer ? 'pg-econ__picked' : undefined}>
                {moneyExact(c.data.guestPays)} {t('pricing.common.currency')}
                <span className="pg-footing">
                  {' '}
                  {c.isOffer && !vatOn
                    ? t('pricing.direct.noVat')
                    : t('pricing.common.incVat')}
                </span>
              </td>
            ))}
          </tr>

          {ROWS.map(field => (
            <tr key={field}>
              <td className="pg-econ__label">
                {field === 'vat'
                  ? t('pricing.quote.vat')
                  : t(`pricing.economics.${field === 'roomRevenueExVat' ? 'roomRevenue' : field}`)}
                {/* Every row states its footing — except the VAT line, which is neither
                    before nor after itself. A row left unlabelled is otherwise the row
                    someone later "corrects" by 15%.
                    ⚠️ `commissionBase` is the one row whose footing FOLLOWS THE BASIS (with
                    VAT under an inclusive basis, without under the exclusive one), and its
                    footing is suppressed when the VAT what-if is OFF: it is then per-COLUMN
                    — the picked column carries a no-VAT base while the two beside it stay
                    inclusive — and one row label cannot be true of all three. */}
                {field !== 'vat' && !(field === 'commissionBase' && !vatOn) && (
                  <span className="pg-footing">
                    {' '}
                    {t(field === 'commissionBase' && billsOnVatInclusive
                      ? 'pricing.common.incVat'
                      : 'pricing.common.exVat')}
                  </span>
                )}
              </td>
              {columns.map(c => (
                <td
                  key={c.key}
                  data-figure
                  className={[
                    field === 'ownerNet' ? 'pg-econ__owner-net' : '',
                    c.isOffer ? 'pg-econ__picked' : ''
                  ].filter(Boolean).join(' ') || undefined}
                >
                  {/* `?? '—'`: on deploy skew an older payload carries no `commissionBase`,
                      and a bare currency symbol reads as zero rather than as unknown. */}
                  {moneyExact(c.data[field]) ?? '—'} {t('pricing.common.currency')}
                </td>
              ))}
            </tr>
          ))}

        </tbody>
      </table>

      {belowFloor && (
        <div className="pg-notice pg-notice--warn">
          {money(nightly)} {t('pricing.common.currency')} {t('pricing.quote.belowFloor')}{' '}
          ({money(floor)} {t('pricing.common.currency')}) · {t('pricing.quote.advisory')}
        </div>
      )}

      {/* The guest sentence only. What it costs the owner is deliberately NOT repeated
          here — صافي المالك above says it per channel, and a line about owner profit is not
          what ops reads out on a call.

          ⚠️ Worded off the CHANNEL the offer was made on. The direct-only phrasing is a
          claim about where the guest books, and on an Airbnb offer it is simply false — the
          guest is booking on Airbnb either way, the price is just lower. */}
      {given > 0 && (
        <div className="pg-notice pg-notice--ok">
          <div className="pg-notice__title">
            {offerChannel === 'direct' ? (
              <>
                {t('pricing.direct.guestPaysNow')} {moneyExact(offer.guestPays)}{' '}
                {t('pricing.common.currency')} {t('pricing.direct.directVia')}{' '}
                {t('pricing.direct.insteadOf')}{' '}
                {moneyExact(scenarioGuestPrice)} {t('pricing.common.currency')}{' '}
                {t('pricing.direct.viaPlatform')} — {t('pricing.direct.guestSaves')}{' '}
                {moneyExact(given)} {t('pricing.common.currency')} ({percent(givenPercent)})
              </>
            ) : (
              <>
                {t('pricing.direct.guestPaysNow')} {moneyExact(offer.guestPays)}{' '}
                {t('pricing.common.currency')} {t('pricing.direct.onChannel')}{' '}
                {t(`pricing.economics.${offerChannel}`)}{' '}
                {t('pricing.direct.insteadOf')}{' '}
                {moneyExact(scenarioGuestPrice)} {t('pricing.common.currency')}{' '}
                — {t('pricing.direct.guestSaves')}{' '}
                {moneyExact(given)} {t('pricing.common.currency')} ({percent(givenPercent)})
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelEconomics;
